import React, { FC, useState } from 'react';
import { createInsightsApolloClient } from 'app/modules/insights/utils/createAuthorizedInsightsApolloClient';
import {
  JsonParam,
  NumericArrayParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { ApolloProvider } from '@apollo/client';
import ReportFailedToBuildMessage from 'app/modules/public/modules/pdfReports/components/ReportFailedToBuildMessage';
import Container from 'components/insights/Container';
import PagePerformancePdfExportMetricChartsCards from 'app/modules/public/modules/pdfReports/modules/insightsPagePerformance/components/PagePerformancePdfExportMetricChartsCards';
import dayjs from 'dayjs';
import { Page } from 'app/modules/insights/types/Page';
import PdfExportCoverPage from 'app/modules/public/modules/pdfReports/components/PdfExportCoverPage';
import { getMetricsProperty } from 'app/modules/insights/modules/pagePerformance/utils/metricProperty';
import { getMetricsForPageType } from 'app/modules/insights/modules/postsPerformance/utils/metricProperty';
import { ANALYTICS_LITE_POST_PERFORMANCE } from 'app/modules/insightsLite/utils/postsPerformanceMetrics';
import { ANALYTICS_LITE_PAGE_PERFORMANCE } from 'app/modules/insightsLite/utils/pagePerformanceMetrics';
import TopPostsForMetric from '../insightsPagePerformance/modules/insightsPagePerformanceOverview/components/TopPostsForMetric';
import { useTranslation } from 'react-i18next';

type Props = {};

const InsightsLitePDFReportRoute: FC<Props> = (props) => {
  const { t } = useTranslation();
  const [query] = useQueryParams({
    token: StringParam,
    startDate: StringParam,
    endDate: StringParam,
    page: JsonParam,
    types: withDefault(NumericArrayParam, []),
    metricKey: StringParam,
    postMetrics: withDefault(NumericArrayParam, []),
  });

  const [client] = useState(() =>
    createInsightsApolloClient(query.token ?? ''),
  );

  if (
    query.token &&
    query.startDate &&
    query.endDate &&
    query.page &&
    query.types &&
    query.metricKey &&
    query.postMetrics
  ) {
    const formattedQueryValues = {
      startDate: dayjs(query.startDate),
      endDate: dayjs(query.endDate),
      page: query.page as Page,
      types: query.types.flatMap((type) => (type !== null ? [type] : [])),
      metric: getMetricsProperty(query.page._type, query.metricKey),
      postMetrics: (() => {
        const metrics = getMetricsForPageType(query.page._type);
        if (!metrics) return [];
        return query.postMetrics
          .map((key) => metrics[key as keyof typeof metrics])
          .filter(
            (metric): metric is NonNullable<typeof metric> => metric != null,
          );
      })(),
    };

    const litePageMetrics =
      ANALYTICS_LITE_PAGE_PERFORMANCE[formattedQueryValues.page._type] || [];
    const litePostMetrics =
      ANALYTICS_LITE_POST_PERFORMANCE[formattedQueryValues.page._type] || [];

    return (
      <ApolloProvider client={client}>
        <Container>
          <div style={{ breakInside: 'avoid' }} className="tw-min-h-[1708px]">
            <PdfExportCoverPage
              page={formattedQueryValues.page}
              startDate={formattedQueryValues.startDate}
              endDate={formattedQueryValues.endDate}
            />
          </div>
          <div style={{ breakInside: 'avoid' }} className="tw-min-h-[1708px]">
            <div className="tw-mb-8 tw-text-xl tw-font-bold">
              {t('pdfReports.sections.postPerformance')}
            </div>
            <TopPostsForMetric
              {...formattedQueryValues}
              metricsToShow={litePostMetrics.map((metric) => metric.key)}
            />
          </div>
          <div style={{ breakInside: 'avoid' }}>
            <div className="tw-mb-8 tw-text-xl tw-font-bold">
              {t('pdfReports.sections.pagePerformance')}
            </div>
            <PagePerformancePdfExportMetricChartsCards
              chartWidth={550}
              page={formattedQueryValues.page}
              selectedMetrics={litePageMetrics.map((m) => m.key)}
            />
          </div>
        </Container>
      </ApolloProvider>
    );
  }

  return <ReportFailedToBuildMessage />;
};

export default InsightsLitePDFReportRoute;
